// Shared quality mappings and lookup logic for camera/light/printer ratings.

export const QUALITY_MAP = {
  CamType: {
    Spiegelreflexkamera: {
      detailsKey: "qualityDetail.cam.dslr",
      ratingKey: "rating.VeryGood",
    },
    "Digitale Systemkamera (DSLM)": {
      detailsKey: "qualityDetail.cam.dslm",
      ratingKey: "rating.VeryGood",
    },
    "4K-Kamera": {
      detailsKey: "qualityDetail.cam.4k",
      ratingKey: "rating.VeryGood",
    },
    "HD-Kamera": {
      detailsKey: "qualityDetail.cam.hd",
      ratingKey: "rating.Good",
    },
  },
  LightType: {
    "Studio-Blitz": {
      detailsKey: "qualityDetail.light.studioFlash",
      ratingKey: "rating.VeryGood",
    },
    "Foto-Blitz": {
      detailsKey: "qualityDetail.light.photoFlash",
      ratingKey: "rating.VeryGood",
    },
    "LED-Blitz": {
      detailsKey: "qualityDetail.light.ledFlash",
      ratingKey: "rating.Good",
    },
    "LED-Dauerlicht": {
      detailsKey: "qualityDetail.light.ledConstant",
      ratingKey: "rating.Good",
    },
  },
};

// Paper capacity guidance per guest volume.
export const PAPER_STORAGE_LOOKUP = [
  {
    guests: 25,
    "0-25": "rating.VeryGood",
    "26-50": "rating.VeryGood",
    "51-110": "rating.VeryGood",
    "111-200": "rating.VeryGood",
    "201-400": "rating.VeryGood",
    "401-500": "rating.VeryGood",
    "501-600": "rating.VeryGood",
    "601-700": "rating.VeryGood",
    "701-800": "rating.VeryGood",
    "801-900": "rating.VeryGood",
    "901-1000": "rating.VeryGood",
  },
  {
    guests: 50,
    "0-25": "rating.VeryGood",
    "26-50": "rating.VeryGood",
    "51-110": "rating.VeryGood",
    "111-200": "rating.VeryGood",
    "201-400": "rating.VeryGood",
    "401-500": "rating.VeryGood",
    "501-600": "rating.VeryGood",
    "601-700": "rating.VeryGood",
    "701-800": "rating.VeryGood",
    "801-900": "rating.VeryGood",
    "901-1000": "rating.VeryGood",
  },
  {
    guests: 75,
    "0-25": "rating.Good",
    "26-50": "rating.Good",
    "51-110": "rating.Good",
    "111-200": "rating.VeryGood",
    "201-400": "rating.VeryGood",
    "401-500": "rating.VeryGood",
    "501-600": "rating.VeryGood",
    "601-700": "rating.VeryGood",
    "701-800": "rating.VeryGood",
    "801-900": "rating.VeryGood",
    "901-1000": "rating.VeryGood",
  },
  {
    guests: 100,
    "0-25": "rating.Good",
    "26-50": "rating.Good",
    "51-110": "rating.Good",
    "111-200": "rating.VeryGood",
    "201-400": "rating.VeryGood",
    "401-500": "rating.VeryGood",
    "501-600": "rating.VeryGood",
    "601-700": "rating.VeryGood",
    "701-800": "rating.VeryGood",
    "801-900": "rating.VeryGood",
    "901-1000": "rating.VeryGood",
  },
  {
    guests: 150,
    "0-25": "rating.NotIdeal",
    "26-50": "rating.Okay",
    "51-110": "rating.Okay",
    "111-200": "rating.Good",
    "201-400": "rating.VeryGood",
    "401-500": "rating.VeryGood",
    "501-600": "rating.VeryGood",
    "601-700": "rating.VeryGood",
    "701-800": "rating.VeryGood",
    "801-900": "rating.VeryGood",
    "901-1000": "rating.VeryGood",
  },
  {
    guests: 200,
    "0-25": "rating.NotIdeal",
    "26-50": "rating.NotIdeal",
    "51-110": "rating.Okay",
    "111-200": "rating.Good",
    "201-400": "rating.VeryGood",
    "401-500": "rating.VeryGood",
    "501-600": "rating.VeryGood",
    "601-700": "rating.VeryGood",
    "701-800": "rating.VeryGood",
    "801-900": "rating.VeryGood",
    "901-1000": "rating.VeryGood",
  },
  {
    guests: 201,
    "0-25": "rating.NotIdeal",
    "26-50": "rating.NotIdeal",
    "51-110": "rating.NotIdeal",
    "111-200": "rating.Okay",
    "201-400": "rating.Good",
    "401-500": "rating.VeryGood",
    "501-600": "rating.VeryGood",
    "601-700": "rating.VeryGood",
    "701-800": "rating.VeryGood",
    "801-900": "rating.VeryGood",
    "901-1000": "rating.VeryGood",
  },
];

// Return rating key (e.g., "rating.Good") for a given paper volume and guest count.
export function getPaperStorageRatingKey(volume, guestCount = 50) {
  if (!volume || volume <= 0) return "rating.NoInfo";

  const closestRow =
    PAPER_STORAGE_LOOKUP.find((row) => guestCount <= row.guests) ||
    PAPER_STORAGE_LOOKUP[PAPER_STORAGE_LOOKUP.length - 1];

  const rangeKey = Object.keys(closestRow).find((range) => {
    if (range === "guests") return false;
    const [start, end] = range.split("-").map((n) => parseInt(n, 10));
    if (isNaN(start)) return false;
    if (!isNaN(end)) return volume <= end;
    return false;
  });

  return rangeKey ? closestRow[rangeKey] : "rating.NotIdeal";
}

// Return max guest count where paper storage package is rated Good or better.
export function getMaxGuestsForPaperStorage(paperStorageSize) {
  if (!paperStorageSize || paperStorageSize <= 0) return null;

  // Find the range for this paper storage size
  const rangeKey = Object.keys(PAPER_STORAGE_LOOKUP[0]).find((range) => {
    if (range === "guests") return false;
    const [start, end] = range.split("-").map((n) => parseInt(n, 10));
    if (isNaN(start)) return false;
    if (!isNaN(end)) return paperStorageSize <= end;
    return false;
  });

  if (!rangeKey) return null;

  // Find rows where rating is Good or VeryGood
  const validRows = PAPER_STORAGE_LOOKUP.filter((row) => {
    const rating = row[rangeKey];
    return rating === "rating.VeryGood" || rating === "rating.Good";
  });

  if (validRows.length === 0) return null;

  // Return the highest guest count with Good or better rating
  return validRows[validRows.length - 1].guests;
}
